/** Minimal shape of a footage row — only the fields coverage cares about. */
export interface ProbedVideoRow {
  start_time: string;
  end_time: string;
}

export type ProbeCoverage =
  /** Footage exists across `[from, to]`. */
  | { kind: "covered"; from: number; to: number }
  /** The window was asked about and holds nothing. */
  | { kind: "empty"; from: number; to: number };

/**
 * What a settled footage probe learned, as the coverage store needs it.
 *
 * Split out from the row's effect so the decision can be tested without
 * rendering the map: the distinction between "asked, nothing there" and "nobody
 * asked" is the whole point of the availability strip, and getting it backwards
 * makes the strip claim "no footage" over a stretch that was never probed.
 *
 * Mirrors the video tile's own recording: the RETURNED ROWS' extent when there
 * is footage (not the probe window — footage outside the rows is unknown, not
 * absent), and the PROBED window when there is none.
 */
export const coverageFromProbe = (
  rows: readonly ProbedVideoRow[] | undefined,
  probedFromMs: number,
  probedToMs: number,
): ProbeCoverage => {
  if (!rows?.length) return { kind: "empty", from: probedFromMs, to: probedToMs };

  let from = Infinity;
  let to = -Infinity;
  for (const row of rows) {
    const start = new Date(row.start_time).getTime();
    const end = new Date(row.end_time).getTime();
    // An unparseable row must not poison the extent into NaN and mark the whole
    // window covered-from-NaN.
    if (Number.isNaN(start) || Number.isNaN(end)) continue;
    from = Math.min(from, start);
    to = Math.max(to, end);
  }
  // Every row was unparseable: that is not evidence of footage.
  if (!Number.isFinite(from) || !Number.isFinite(to)) {
    return { kind: "empty", from: probedFromMs, to: probedToMs };
  }
  return { kind: "covered", from, to };
};
